// Site-side client for the Sinergie Engine local HTTP server. Talks
// directly to http://127.0.0.1:8787 — no browser extension involved.
// Contract: POST /chat with {messages, temperature, max_tokens, schema?},
// response is a stream of newline-delimited `data: {...}` SSE lines typed
// progress|token|done|error (see docs/superpowers/specs/
// 2026-08-27-native-engine-rust-design.md).
import {
  buildMentorMessages,
  DT_PHASES,
  canAdvancePhase,
  resolveTeacherStage,
  openDecisionsForPhase,
  RAG_RELEVANCE_THRESHOLD,
} from "./mentorPrompt";
import { pickMove, classifyMove } from "./moveRouter";
import { classifyIntent, resolveIntentForMode } from "./intentRouter";
import { buildBrainstormMessages, parseBrainstormJSON } from "./brainstormPrompt";
import { buildSummarizeMessages, parseSummarize, bootstrapBrief, mergeCantiere, mergeFondamenta, mergeDecisions, EMPTY_CANTIERE, EMPTY_FONDAMENTA } from "./summarize";
import {
  buildScenarioActionMessages, buildScenarioActionSchema, extractValidNodeIds,
  parseScenarioProposal, readScenarioTreeState, decideScenarioAction,
} from "./scenarioActionPrompt";
import { userTurns } from "./chatHistory";
import { vocab } from "./prompts";
import {
  fetchKnowledge,
  parseMentorJSON,
  finalizeMentorParsed,
  getSessionMemory,
} from "./slmMentor";
import { resolveSessionMode, resolveCheckpoint } from "./slmMentor";

export const ENGINE_MODEL_LABEL = "Qwen 2.5 · 7B (Engine)";

const ENGINE_BASE = "http://127.0.0.1:8787";

// Full `/health` snapshot: `{ ok, modelPresent, downloadPct }` — or a not-ok
// snapshot on any failure/timeout. `downloadPct` is 0..100 while the engine is
// pulling the GGUF, otherwise null. Lets the mentor distinguish "engine off"
// from "engine on, model still downloading, N%".
export async function engineHealth(timeoutMs = 800) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const timeoutGuard = new Promise((resolve) => {
    controller.signal.addEventListener("abort", () =>
      resolve({ ok: false, modelPresent: false, downloadPct: null }));
  });
  try {
    return await Promise.race([
      fetch(`${ENGINE_BASE}/health`, { signal: controller.signal })
        .then(async (res) => {
          if (!res.ok) return { ok: false, modelPresent: false, downloadPct: null };
          const b = typeof res.json === "function"
            ? await res.json().catch(() => ({}))
            : {};
          const pct = b?.model?.download_pct;
          return {
            ok: true,
            modelPresent: !!b?.model?.present,
            downloadPct: typeof pct === "number" ? pct : null,
            // Live label of whichever model the tray is set to — the engine
            // resolves it per request, so this can change between polls.
            modelLabel: typeof b?.model?.label === "string" ? b.model.label : null,
          };
        })
        .catch(() => ({ ok: false, modelPresent: false, downloadPct: null })),
      timeoutGuard,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export async function detectEngine(timeoutMs = 800) {
  return (await engineHealth(timeoutMs)).ok;
}

// Parses one buffered SSE response body (see the plan's note on why this
// isn't yet a live streaming reader) into an ordered array of event
// objects, e.g. [{type:"token",token:"a"},{type:"done",content:"a"}].
function parseSseEvents(text) {
  const events = [];
  for (const block of text.split("\n\n")) {
    const line = block.trim();
    if (!line.startsWith("data:")) continue;
    const payload = line.slice("data:".length).trim();
    if (!payload) continue;
    try {
      events.push(JSON.parse(payload));
    } catch (e) {
      // Malformed line — skip rather than crash the whole request.
    }
  }
  return events;
}

export function engineRequest(msg, { onEvent, timeoutMs = 300000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  // Same rationale as detectEngine's timeoutGuard: enforce the deadline via
  // the abort signal directly, independent of whether the fetch() promise
  // itself ever settles (a hung connection must still reject on schedule).
  const timeoutGuard = new Promise((_, reject) => {
    controller.signal.addEventListener("abort", () => reject(new Error("engine timeout")));
  });

  const fetchPromise = fetch(`${ENGINE_BASE}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(msg),
    signal: controller.signal,
  }).then(async (res) => {
    // A non-SSE HTTP failure (a 4xx/5xx from the engine itself, a CORS-
    // rejected response, a proxy error page) has no `data:` lines, so parsing
    // it would yield zero events and surface as the misleading "engine
    // returned no result". Fail with the real status instead.
    if (!res.ok) throw new Error(`engine HTTP ${res.status}`);
    const text = await res.text();
    const events = parseSseEvents(text);
    let result = null;
    for (const e of events) {
      if (e.type === "error") throw new Error(e.message || e.code || "engine error");
      if (e.type === "done") {
        result = e;
        continue;
      }
      if (onEvent) onEvent(e);
    }
    if (!result) throw new Error("engine returned no result");
    return result;
  });

  return Promise.race([fetchPromise, timeoutGuard])
    .catch((e) => {
      if (e.name === "AbortError") throw new Error("engine timeout");
      throw e;
    })
    .finally(() => clearTimeout(timer));
}

// These helper calls generate only a handful of tokens, but any of them can
// be the FIRST call of a turn and therefore the one that pays for a cold
// start: `engineGenerate` drives resolveSessionMode/resolveCheckpoint/
// classifyMove (first after an engine or llama-server restart mid-session),
// and `engineGenerateClassifyIntent` runs first on every resumed session
// (prevMode restored from Firestore). A 7B CUDA load takes ~20-60s, so the
// old 8s/15s budgets aborted deterministically before the model was ever in
// VRAM. 180s covers a cold load with margin.
// This is a blunt fix: the real one is re-arming the timer on stream activity
// so the deadline measures silence rather than total elapsed time — deferred
// to the streaming follow-up along with live token streaming.
const HELPER_TIMEOUT_MS = 180000;

export async function engineGenerate(prompt) {
  const res = await engineRequest(
    { type: "chat", messages: [{ role: "user", content: prompt }], temperature: 0.2, max_tokens: 24 },
    { timeoutMs: HELPER_TIMEOUT_MS });
  return res.content || "";
}

async function engineGenerateClassifyIntent(prompt) {
  const res = await engineRequest(
    { type: "chat", messages: [{ role: "user", content: prompt }], temperature: 0.2, max_tokens: 200 },
    { timeoutMs: HELPER_TIMEOUT_MS });
  return res.content || "";
}

/**
 * Client-side equivalent of the old backend `POST /resummarize`: rebuild the
 * session brief (objective + process summary + cantiere/fondamenta) from a
 * bounded recent chat window, through the local engine. v1 has no Gemini, so
 * this manual "regenerate summary" action runs on the local model like every
 * other mentor call. Returns `{ obiettivo, sintesi, cantiere, fondamenta }`.
 */
export async function engineResummarize({ chat, usedCards = [], prevBrief = null, currentPhase = "esplorazione_ideazione", lang = "it" }) {
  const res = await engineRequest(
    {
      type: "chat",
      messages: buildSummarizeMessages({
        prevSummary: prevBrief?.sintesi_processo || "",
        prevObjective: prevBrief?.obiettivo || "",
        exchange: chat,
        cards: usedCards,
        prevCantiere: prevBrief?.cantiere || null,
        prevFondamenta: prevBrief?.fondamenta || null,
        prevDecisions: prevBrief?.decisions || null,
        lang,
      }),
      temperature: 0.3,
      max_tokens: 400,
    },
    { timeoutMs: 120000 }
  );
  const { obiettivo, sintesi, cantiere, fondamenta, nuoveDecisioni, aggiornaDecisioni } = parseSummarize(
    res.content || "", { currentPhase, validPhases: DT_PHASES }
  );
  return {
    obiettivo,
    sintesi,
    cantiere: mergeCantiere(prevBrief?.cantiere, cantiere),
    fondamenta: mergeFondamenta(prevBrief?.fondamenta, fondamenta),
    decisions: mergeDecisions(prevBrief?.decisions, nuoveDecisioni, aggiornaDecisioni),
  };
}

// `t` (optional, from Chatbot.jsx's useT()) translates the progress copy
// below — defaults reproduce the original Italian so callers/tests that
// don't pass one (this module runs outside React) see unchanged behavior.
const DEFAULT_T = (key, vars) => {
  const dict = {
    "chat.progress.downloadingModelLocal": "Un attimo, sto scaricando il modello in locale… {pct}% (una sola volta)",
    "chat.progress.loadingGpu": "Un attimo, carico il modello nella GPU…",
  };
  let s = dict[key] || key;
  if (vars) for (const k in vars) s = s.replace(`{${k}}`, vars[k]);
  return s;
};

function withProgress(onProgress, t = DEFAULT_T) {
  return {
    onEvent: (e) => {
      if (e.type === "progress" && onProgress) {
        onProgress({
          text: e.status === "downloading"
            ? t("chat.progress.downloadingModelLocal", { pct: e.pct })
            : (e.note || t("chat.progress.loadingGpu")),
          progress: e.status === "downloading" ? (e.pct || 0) / 100 : 0,
        });
      }
    },
  };
}

// The default transport: the native local engine. `runEngineMentor` takes an
// `llm` of this shape so the BYOK cloud path (cloudProviders.cloudLlm) can
// reuse the entire pipeline — only the 4 model calls change.
const engineLlm = {
  request: ({ messages, temperature = 0.4, max_tokens = 1024, schema }, opts) =>
    engineRequest(
      { type: "chat", messages, temperature, max_tokens, ...(schema ? { schema } : {}) },
      opts || {}
    ),
  generate: engineGenerate,
  generateClassifyIntent: engineGenerateClassifyIntent,
};

/**
 * Full mentor turn. Default transport is the native engine; pass `llm` (see
 * `engineLlm` / `cloudProviders.cloudLlm`) to run the same pipeline against a
 * user-supplied cloud key. Same inputs/outputs as runSlmMentor so Chatbot can
 * route to either transparently.
 */
export async function runEngineMentor(
  { sessionId, chat, purpose, usedCards = [], unusedCards = [], prevBrief = null, prevPhase = "esplorazione_ideazione", prevMode = null, prevModeAskAttempts = 0, prevPendingCheckpoint = false, prevTeacherStage = "osserva", scenarioTree = "", likelyBranchId = null, lang = "it" },
  { onProgress, llm = engineLlm, t } = {}
) {
  const rawIntent = (!prevMode)
    ? "mentor"
    : await classifyIntent({ chat, generate: llm.generateClassifyIntent, lang });
  const intent = resolveIntentForMode(rawIntent, prevMode);

  const decisionsForPhase = openDecisionsForPhase(prevBrief, prevPhase);
  const decisionsText = decisionsForPhase.map((d) => d.text).join("; ");

  if (intent === "brainstorm") {
    // Retrieval query against the Italian book chunks — internal, not
    // localized (" — decisioni:" included).
    let query = `Patterns and connections related to: ${purpose} and cards: ${usedCards.map((c) => c.label)}`;
    if (decisionsText) query += ` — decisioni: ${decisionsText}`;
    const { text: knowledge } = await fetchKnowledge(query, 3, lang);
    const { messages } = buildBrainstormMessages({ purpose, usedCards, unusedCards, knowledge, lang });
    const res = await llm.request(
      { messages, temperature: 0.4, max_tokens: 1024 },
      withProgress(onProgress, t)
    );
    return parseBrainstormJSON(res.content || "");
  }

  const memory = getSessionMemory(sessionId);
  const currentPhase = prevPhase;

  let mode = prevMode;
  let modeAskAttempts = prevModeAskAttempts;
  let forceModeQuestion = false;
  let forceModeRedirect = false;
  let modeJustResolved = false;
  if (intent === "mentor") {
    ({ mode, modeAskAttempts, forceModeQuestion, forceModeRedirect, modeJustResolved } = await resolveSessionMode({
      chat, prevMode, prevModeAskAttempts, lang, generate: llm.generate,
    }));
  }

  let forceCheckpoint = false;
  let advanceAllowed = false;
  let pendingCheckpoint = false;
  if (intent === "mentor" && !forceModeQuestion && !forceModeRedirect) {
    const canAdvanceNow = DT_PHASES.indexOf(currentPhase) < DT_PHASES.length - 1
      && canAdvancePhase(prevBrief?.evidence_slots, currentPhase);
    ({ forceCheckpoint, advanceAllowed, pendingCheckpoint } = await resolveCheckpoint({
      chat, prevPendingCheckpoint, canAdvance: canAdvanceNow, lang, generate: llm.generate,
    }));
  }

  let move = "question";
  if (intent === "mentor" && !forceModeQuestion && !forceModeRedirect && !forceCheckpoint) {
    const { move_type: routed, needsLLM: moveNeedsLLM } = pickMove({ chat, lang });
    move = moveNeedsLLM ? await classifyMove({ chat, generate: llm.generate, mode, lang }) : routed;
  }

  // teacherStageForPrompt must be known BEFORE the RAG fetch (only intervento
  // fetches at all) — see slmMentor.js's runSlmMentor for the same reorder.
  const { teacherStageForPrompt } = resolveTeacherStage({
    prevTeacherStage, advanceAllowed, forceModeQuestion, forceModeRedirect, forceCheckpoint, modeJustResolved, intent,
  });

  let knowledge = "";
  let needsClarification = false;
  if (intent === "knowledge") {
    const turns = userTurns(chat);
    ({ text: knowledge } = await fetchKnowledge(turns[turns.length - 1] || "", 3, lang));
  } else if (intent === "mentor" && teacherStageForPrompt === "intervento") {
    const turns = userTurns(chat);
    const lastUserDubbio = turns[turns.length - 1] || "";
    const parts = [purpose, lastUserDubbio, decisionsText].filter(Boolean);
    const query = parts.length ? parts.join(" — ") : `Design thinking ${currentPhase}`;
    const { text, minDistance } = await fetchKnowledge(query, 3, lang);
    if (minDistance == null || minDistance > RAG_RELEVANCE_THRESHOLD) {
      needsClarification = true;
    } else {
      knowledge = text;
    }
  }

  const { messages, allLabels, wrapUpEligible, teacherStageOut, teacherStageIn } = buildMentorMessages({
    chat, purpose, usedCards, unusedCards, knowledge, memory,
    move, prevBrief: prevBrief || null, intent, prevPhase,
    mode, forceModeQuestion, forceModeRedirect, modeJustResolved, forceCheckpoint, advanceAllowed,
    prevTeacherStage,
    scenarioTree, likelyBranchId,
    needsClarification,
    lang,
  });

  const res = await llm.request(
    { messages, temperature: 0.4, max_tokens: 1536 },
    withProgress(onProgress, t)
  );

  const parsed = parseMentorJSON(res.content || "", currentPhase, lang);
  const finalized = finalizeMentorParsed(parsed, { sessionId, allLabels, currentPhase, prevBrief: prevBrief || null, intent, advanceAllowed, mode, forceModeQuestion, forceModeRedirect, modeJustResolved, wrapUpEligible, teacherStageOut, teacherStageIn });

  finalized.brief.obiettivo = finalized.brief.obiettivo || prevBrief?.obiettivo || "";
  finalized.brief.sintesi_processo = finalized.brief.sintesi_processo || prevBrief?.sintesi_processo || "";
  finalized.brief.cantiere = prevBrief?.cantiere || EMPTY_CANTIERE;
  finalized.brief.fondamenta = prevBrief?.fondamenta || EMPTY_FONDAMENTA;
  finalized.brief.decisions = prevBrief?.decisions || [];
  if (intent === "mentor") {
    try {
      const turns = userTurns(chat);
      const lastUser = turns[turns.length - 1] || "";
      const { user_label: userLabel, mentor_label: mentorLabel } = vocab(lang);
      const exchange = `${userLabel}: ${lastUser}\n${mentorLabel}: ${finalized.message}`;
      const sumRes = await llm.request(
        {
          messages: buildSummarizeMessages({
            prevSummary: prevBrief?.sintesi_processo || "",
            prevObjective: prevBrief?.obiettivo || "",
            exchange,
            cards: usedCards,
            prevCantiere: prevBrief?.cantiere || null,
            prevFondamenta: prevBrief?.fondamenta || null,
            prevDecisions: prevBrief?.decisions || null,
            lang,
          }),
          temperature: 0.3,
          max_tokens: 400,
        },
        { timeoutMs: 120000 }
      );
      const { obiettivo, sintesi, cantiere, fondamenta, nuoveDecisioni, aggiornaDecisioni } = parseSummarize(
        sumRes.content || "", { currentPhase, validPhases: DT_PHASES }
      );
      if (obiettivo) finalized.brief.obiettivo = obiettivo;
      if (sintesi) finalized.brief.sintesi_processo = sintesi;
      finalized.brief.cantiere = mergeCantiere(prevBrief?.cantiere, cantiere);
      finalized.brief.fondamenta = mergeFondamenta(prevBrief?.fondamenta, fondamenta);
      finalized.brief.decisions = mergeDecisions(prevBrief?.decisions, nuoveDecisioni, aggiornaDecisioni);
    } catch (e) {
      console.warn("engine summarize pass failed (non-fatal):", e);
    }
  }
  bootstrapBrief(finalized.brief, purpose, lang);

  if (intent === "mentor" && mode === "scenari_futuri" && !modeJustResolved) {
    try {
      const validNodeIds = extractValidNodeIds(scenarioTree);
      const actionMessages = buildScenarioActionMessages({ chat, scenarioTree, cardLabels: allLabels, likelyBranchId, lang }).messages;
      const actionRes = await llm.request(
        {
          messages: actionMessages,
          schema: buildScenarioActionSchema({ validNodeIds, cardLabels: allLabels }),
          temperature: 0.3,
          max_tokens: 600,
        },
        { timeoutMs: 120000 }
      );
      const { proposal } = parseScenarioProposal(actionRes.content || "", {
        validNodeIds, cardLabels: allLabels,
      });
      const { fork, card } = decideScenarioAction(proposal, {
        treeState: readScenarioTreeState(scenarioTree),
        usedCardLabels: usedCards.map((c) => c.label),
      });
      finalized.suggested_splits = fork ? [fork] : [];
      finalized.suggested_cards = card ? [{ label: card, zone: currentPhase }] : [];
    } catch (e) {
      console.error("[scenario-action:engine] threw (non-fatal, main call's own suggestions kept):", e);
    }
  }

  finalized.mode = mode;
  finalized.modeAskAttempts = modeAskAttempts;
  finalized.pendingCheckpoint = pendingCheckpoint;
  return finalized;
}
